<script setup>
/**
 * "Your notes on {Restaurant}" — every dated note for one restaurant, newest
 * first, with add, edit and remove.
 *
 * ONE dialog, opened from Favorites and from a restaurant's My Notes tab
 * ("+Add/Update notes that opens up the Note pop-up", Kapil, 2026-09-22), so
 * the two surfaces cannot drift. It used to live inline in MemberFavorites.
 *
 * Adding writes a NEW entry — it never silently replaces what the member told
 * RM before. Editing and removing are per entry. After a new note is saved the
 * member gets the same sharing decision RM AI offers, with no model involved:
 * notes belong to every member, and RM AI is Platinum-only. Every note says
 * what it is — Private Only, or shared — with Share / Make private beside it,
 * the same as the My Notes tab (Kapil, 2026-09-22).
 *
 * Saved people are listed too (2026-09-23), the way My Notes shows them: the
 * member's words, the contact line, the card, "Contact · Private Only", and
 * Edit / Remove — never Share, because a contact is never shared. This dialog
 * used to leave them out, so a card the Favorites row had just shown vanished
 * the moment the member tapped "+1 more note".
 *
 * Editing happens in place (Kapil, 2026-09-23: "if I am editing a current note,
 * I should do it in line versus at the bottom of the screen"), through
 * ProfileNoteItem. One note, one text (2026-09-24): a note that is shared IS
 * the Highlight, so each note has one Edit and one status. The box at the
 * bottom only adds a new note.
 */
import { ref, watch } from 'vue'
import axios from '@axios'
import { useRestaurantsStore } from '@/stores'
import { NOTE_MAX_LENGTH } from '@/utils/profileNotes'
import { relativeAgo } from '@/utils/relativeAgo'
import ProfileShareDecision from '@core/components/ProfileShareDecision.vue'
import ProfileNoteItem from '@core/components/ProfileNoteItem.vue'
import { activeShares, entryAt, isContactEntry, onNetwork, removeQuestion, restaurantShareOpen, statusLine } from '@/utils/noteDisplay'
import { mediaUrl } from '@core/utils/mediaUrl'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  restaurantId: { type: String, default: '' },
  restaurantName: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'changed'])

const restaurantsStore = useRestaurantsStore()

const entries = ref([])
const loading = ref(false)
const error = ref('')
const text = ref('')
const editingId = ref(null)
const saving = ref(false)

// Set after a NEW note is saved: the member decides who it is for.
const shareDecision = ref(null)

// Sharing a note that was kept private until now.
const sharingEntryId = ref(null)

// A saved card, opened full size in place.
const cardDialogImageId = ref(null)

// Whose words, what status — decided once, in utils/noteDisplay. One note, one
// text, one status (Kapil, 2026-09-24).
const isContact = isContactEntry
const status = entry => statusLine(entry, props.restaurantName)

async function load() {
  if (!props.restaurantId) return
  loading.value = true
  try {
    const { data } = await axios.get('/profile/entries', { params: { restaurantId: props.restaurantId, limit: 50 } })

    entries.value = (Array.isArray(data) ? data : []).filter(Boolean)
    error.value = ''
  } catch {
    entries.value = []
    error.value = 'Could not load your notes. Please try again.'
  } finally {
    loading.value = false
  }
}

watch(() => [props.modelValue, props.restaurantId], ([open]) => {
  if (!open) return
  text.value = ''
  editingId.value = null
  shareDecision.value = null
  sharingEntryId.value = null
  error.value = ''
  load()
}, { immediate: true })

function close() {
  emit('update:modelValue', false)
}

async function afterChange() {
  await Promise.all([load(), restaurantsStore.refreshFavoriteNotes()])
  emit('changed')
}

// In place: the note becomes a box where it is (ProfileNoteItem).
function startEdit(entry) {
  editingId.value = entry.entryId
}

function doneEditing() {
  editingId.value = null
}

async function save() {
  const value = (text.value || '').trim()
  if (!value) return
  saving.value = true
  try {
    // A new thing they are telling RM — a new dated entry, the older ones
    // untouched. Edits happen in place, on the note itself.
    const { data } = await axios.post('/profile/entries', { text: value, restaurantId: props.restaurantId })
    const newEntryId = data?.entryId || null

    text.value = ''
    await afterChange()

    if (newEntryId) {
      // Their exact words, for them to edit if they want. RM does not guess at
      // what is private in a typed note; they know, and it does not.
      const saved = entries.value.find(e => e.entryId === newEntryId)

      shareDecision.value = {
        entryId: newEntryId,
        canShareWithRestaurant: restaurantShareOpen(saved),
      }
    }
  } catch (e) {
    error.value = e?.response?.data?.message || 'Could not save that note. Please try again.'
  } finally {
    saving.value = false
  }
}

// Stops every audience seeing it at once — one status, so one action undoes
// it. Their note is untouched.
async function makePrivate(entry) {
  saving.value = true
  try {
    for (const sh of activeShares(entry)) {
      // eslint-disable-next-line no-await-in-loop
      await axios.delete(`/profile/shares/${sh.shareId}`)
    }
    await afterChange()
  } catch (e) {
    error.value = e?.response?.data?.message || 'Could not make that private right now.'
  } finally {
    saving.value = false
  }
}

async function onShared() {
  sharingEntryId.value = null
  await afterChange()
}

// Removing a member's own words is irreversible from here, so it is always confirmed.
async function remove(entry) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(removeQuestion(entry))) return
  saving.value = true
  try {
    await axios.delete(`/profile/entries/${entry.entryId}`)
    await afterChange()
  } catch {
    error.value = 'Could not remove that note. Please try again.'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="460"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="note-dialog-card">
      <div class="note-dialog-header">
        <VIcon
          icon="mdi-note-text-outline"
          size="20"
          color="primary"
        />
        <span class="note-dialog-title">Your notes on {{ restaurantName }}</span>
      </div>

      <VCardText class="note-dialog-body">
        <div
          v-if="loading && !entries.length"
          class="note-entries__loading"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="20"
            width="2"
          />
        </div>
        <ul
          v-else-if="entries.length"
          class="note-entries"
        >
          <li
            v-for="entry in entries"
            :key="entry.entryId"
            class="note-entry"
          >
            <ProfileNoteItem
              :entry="entry"
              :restaurant-name="restaurantName"
              text-class="note-entry__text"
              :editing="editingId === entry.entryId"
              @open-card="cardDialogImageId = $event"
              @done="doneEditing"
              @changed="afterChange"
            >
              <!--
                Under the note: when, then what it is, then what they can do
                (Kapil, 2026-09-24: "Underneath the timestamp, it should show
                highlight shared with your network and NAIA. That's it.").
              -->
              <template #meta>
                <p class="note-entry__time">
                  {{ relativeAgo(entryAt(entry)) }} ago
                </p>
                <p class="note-entry__status">
                  {{ status(entry) }}
                </p>
                <div class="note-entry__meta">
                  <button
                    v-if="editingId !== entry.entryId"
                    type="button"
                    class="note-entry__action"
                    @click="startEdit(entry)"
                  >
                    Edit
                  </button>
                  <button
                    v-if="!isContact(entry) && !onNetwork(entry) && sharingEntryId !== entry.entryId"
                    type="button"
                    class="note-entry__action"
                    @click="sharingEntryId = entry.entryId"
                  >
                    Share
                  </button>
                  <button
                    v-if="!isContact(entry) && activeShares(entry).length"
                    type="button"
                    class="note-entry__action"
                    :disabled="saving"
                    @click="makePrivate(entry)"
                  >
                    Make private
                  </button>
                  <button
                    type="button"
                    class="note-entry__action"
                    :disabled="saving"
                    @click="remove(entry)"
                  >
                    Remove
                  </button>
                </div>
                <ProfileShareDecision
                  v-if="sharingEntryId === entry.entryId"
                  class="mt-2"
                  :entry-id="entry.entryId"
                  :restaurant-name="restaurantName"
                  :can-share-with-restaurant="restaurantShareOpen(entry)"
                  :just-saved="false"
                  @done="sharingEntryId = null"
                  @shared="onShared"
                />
              </template>
            </ProfileNoteItem>
          </li>
        </ul>
        <p
          v-else
          class="note-entries__empty"
        >
          Nothing saved here yet.
        </p>

        <p
          v-if="error"
          class="note-entries__error"
        >
          {{ error }}
        </p>

        <!-- Straight after a new note: who is it for? The same decision as RM AI. -->
        <ProfileShareDecision
          v-if="shareDecision"
          class="mt-3"
          :entry-id="shareDecision.entryId"
          :restaurant-name="restaurantName"
          :can-share-with-restaurant="shareDecision.canShareWithRestaurant"
          @done="shareDecision = null"
          @shared="afterChange"
        />

        <VTextarea
          v-else
          v-model="text"
          placeholder="Add a note — favorite dishes, tables, team members, occasions, who recommended…"
          :counter="NOTE_MAX_LENGTH"
          :maxlength="NOTE_MAX_LENGTH"
          rows="3"
          auto-grow
          variant="outlined"
          density="compact"
          hide-details="auto"
          class="note-textarea mt-2"
        />
      </VCardText>

      <VCardActions class="note-dialog-actions">
        <VSpacer />
        <VBtn
          variant="text"
          size="small"
          @click="close"
        >
          Close
        </VBtn>
        <VBtn
          v-if="!shareDecision"
          color="primary"
          variant="flat"
          size="small"
          rounded="lg"
          :disabled="!text.trim()"
          :loading="saving"
          @click="save"
        >
          Add note
        </VBtn>
      </VCardActions>

      <!-- A saved card, full size, on top of the notes — the same as My Notes. -->
      <VDialog
        :model-value="!!cardDialogImageId"
        max-width="560"
        @update:model-value="cardDialogImageId = null"
      >
        <VCard class="note-card-dialog">
          <img
            v-if="cardDialogImageId"
            :src="mediaUrl(cardDialogImageId, 'original')"
            alt="Saved business card"
          >
          <div class="note-card-dialog__actions">
            <VBtn
              variant="text"
              size="small"
              @click="cardDialogImageId = null"
            >
              Close
            </VBtn>
          </div>
        </VCard>
      </VDialog>
    </VCard>
  </VDialog>
</template>

<style scoped>
.note-card-dialog { padding: 12px; border-radius: 12px; }
.note-card-dialog img { display: block; width: 100%; height: auto; border-radius: 8px; }
.note-card-dialog__actions { display: flex; justify-content: flex-end; margin-top: 4px; }

.note-dialog-card {
  border-radius: var(--ds-radius-lg) !important;
  overflow: hidden;
}

.note-dialog-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  padding: var(--ds-space-5) var(--ds-space-6) var(--ds-space-2);
}

.note-dialog-title {
  font-size: var(--ds-text-md);
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.87);
}

.note-dialog-body {
  padding: 12px 24px 8px !important;
}

.note-dialog-actions {
  padding: 8px 20px 16px !important;
}

.note-entries {
  margin: 0;
  padding: 0;
  list-style: none;
}

.note-entry {
  padding: 8px 0;
  border-bottom: 1px solid var(--ds-border-subtle);
}

.note-entry:last-child {
  border-bottom: none;
}

.note-entry__text {
  margin: 0;
  font-size: 15px;
  line-height: 1.4;
  white-space: pre-line;
  overflow-wrap: anywhere;
  color: var(--ds-text-primary, inherit);
}

.note-entry__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin-top: 4px;
  font-size: 12.5px;
  color: var(--ds-text-body);
}

/* When, then what it is — each on its own line (Kapil, 2026-09-24). */
.note-entry__time {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: var(--ds-text-body);
}

/* What the note IS outranks when it was written. */
.note-entry__status {
  margin: 2px 0 0;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ds-text-body);
}

.note-entry__action {
  background: none;
  border: none;
  padding: 0;
  white-space: nowrap;
  font-size: 12.5px;
  color: var(--ds-brand-primary-dark);
  cursor: pointer;
}

.note-entries__error {
  margin: 6px 0 0;
  font-size: 13px;
  color: rgb(var(--v-theme-error));
}

.note-entries__empty,
.note-entries__loading {
  margin: 4px 0 0;
  font-size: 13.5px;
  color: var(--ds-text-body);
  line-height: 1.3;
}

.note-textarea :deep(.v-field) {
  border-radius: var(--ds-radius-md) !important;
}

/* 16px minimum, or iOS zooms the page when the field is focused. */
.note-textarea :deep(textarea) {
  font-size: 16px;
}
</style>
